<script>
import { cloneDeep } from "lodash-es";

import Tooltip from "@/components/article/Tooltip.vue";
import lang from "@/directives/lang";

import { casusLabels, tenseLabels } from "./labels";

export default {
  name: "VerbInflectionSchemeModal",
  components: {
    Tooltip
  },
  directives: {
    lang
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    inflectionScheme: {
      type: Object,
      required: true
    },
    lemma: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      casusLabels,
      tenseLabels,
      toolTipText: `Brug " j' " når efterfølgende ord starter med vokaler eller stumt h`,
      activeTabs: {
        indicative: true,
        perfect_tenses: false,
        subjunctive: false,
        conditional: false,
        imperative: false
      },
      allowedTensesMap: {
        indicative: ["present", "imperfect", "future"],
        perfect_tenses: ["present_perfect", "past_perfect", "future_perfect"],
        subjunctive: ["present", "past"],
        conditional: ["present", "past"],
        imperative: ["present"]
      },
      personLabels: {
        singular: {
          first_person_singular: "je/j'",
          second_person_singular: "tu",
          third_person_singular: "il, elle, on"
        },
        plural: {
          first_person_plural: "nous",
          second_person_plural: "vous",
          third_person_plural: "ils, elles"
        }
      }
    };
  },
  computed: {
    filteredInflectionScheme() {
      const scheme = cloneDeep(this.inflectionScheme);
      const validTabs = Object.keys(this.activeTabs);

      for (const [casus, tenses] of Object.entries(scheme)) {
        // Remove tabs (casus) that we don't use
        if (!validTabs.includes(casus)) {
          delete scheme[casus];
          continue;
        }

        for (const [tense, persons] of Object.entries(tenses)) {
          if (!this.allowedTensesMap[casus].includes(tense)) {
            delete scheme[casus][tense];
            continue;
          }

          // Simplify third_person_*_singular to third_person_singular - they contain the same data
          scheme[casus][tense].third_person_singular =
            scheme[casus][tense].third_person_he_singular;
          delete scheme[casus][tense].third_person_he_singular;
          delete scheme[casus][tense].third_person_she_singular;
          delete scheme[casus][tense].third_person_it_singular;

          // Filter to unique indices, as there are many duplicates that would make showing the "/" for some words very complex
          let knownIndices = [];
          let lastPerson = "";
          for (const [person, personData] of Object.entries(persons)) {
            if (!personData) {
              continue;
            }

            if (person !== lastPerson) {
              knownIndices = [];
            }

            scheme[casus][tense][person] = scheme[casus][tense][person].filter((item) => {
              const { prefix, inflexion, suffix } = item;
              // We don't use person as there are also duplicates for ils/elles
              const indexWithoutPerson = prefix + inflexion + suffix;
              if (knownIndices.includes(indexWithoutPerson)) {
                return false;
              }

              knownIndices.push(indexWithoutPerson);
              return true;
            });

            lastPerson = person;
          }
        }
      }

      return scheme;
    }
  },
  methods: {
    buildColumnValue(personData) {
      if (!personData) {
        return "-";
      }

      let value = "";
      const firstValue = personData[0];
      const secondValue = personData[1];

      for (let string of [firstValue.prefix, firstValue.inflexion, firstValue.suffix]) {
        string = string?.trim();

        if (!string) {
          continue;
        }

        value += `${string} `;
      }
      value = value.trim();

      if (secondValue?.inflexion) {
        value += ` / ${secondValue.inflexion}`;
      }

      return value;
    },
    showTab(name) {
      for (const key of Object.keys(this.activeTabs)) {
        this.activeTabs[key] = false;
      }

      this.activeTabs[name] = true;
    }
  }
};
</script>

<template>
  <div v-lang:fr>
    <div class="inflection-scheme-modal__tabs">
      <template v-for="(scheme, tab) in filteredInflectionScheme" :key="tab">
        <button
          class="inflection-scheme-modal__tab-button"
          :class="{
            'inflection-scheme-modal__tab-button--active': activeTabs[tab] === true
          }"
          @click="showTab(tab)"
        >
          {{ casusLabels[tab] }}
        </button>
      </template>
    </div>
    <div v-for="(casus, casusKey) in filteredInflectionScheme" :key="casusKey">
      <table v-if="activeTabs[casusKey] === true" class="table inflection-scheme-modal__table">
        <thead>
          <tr class="inflection-scheme-modal__tr">
            <th scope="col" class="inflection-scheme-modal__th"></th>
            <th
              v-for="(tense, tenseKey) in casus"
              :key="tenseKey"
              scope="col"
              class="inflection-scheme-modal__th"
            >
              {{ tenseLabels[tenseKey] }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(label, person, index) in personLabels.singular"
            :key="`singular-${person}`"
            class="inflection-scheme-modal__tr"
            :class="{
              'inflection-scheme-modal__tr--singular-last':
                index === Object.keys(personLabels.singular).length - 1
            }"
          >
            <td class="inflection-scheme-modal__td">
              <span v-if="casusKey === 'subjunctive'">que </span>
              {{ label }}
              <Tooltip
                v-if="person === 'first_person_singular'"
                variant="secondary"
                :margin-bottom="8"
                :text="toolTipText"
              />
            </td>
            <td
              v-for="(tense, tenseKey) in casus"
              :key="tenseKey"
              class="inflection-scheme-modal__td"
            >
              {{ buildColumnValue(casus[tenseKey][person]) }}
            </td>
          </tr>
          <tr>
            <th scope="col" class="inflection-scheme-modal__table-delimiter"></th>
          </tr>
          <tr
            v-for="(label, person, index) in personLabels.plural"
            :key="`plural-${label}`"
            class="inflection-scheme-modal__tr"
            :class="{ 'inflection-scheme-modal__tr--plural-first': index === 0 }"
          >
            <td class="inflection-scheme-modal__td">
              <span v-if="casusKey === 'subjunctive'">que </span>
              {{ label }}
            </td>
            <td
              v-for="(tense, tenseKey) in casus"
              :key="tenseKey"
              class="inflection-scheme-modal__td"
            >
              {{ buildColumnValue(casus[tenseKey][person]) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
